"use client";

import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { createClientLogger } from "@/lib/logging/client";
import { registerQueryClient } from "@/lib/query-client-registry";

const logger = createClientLogger("components/providers/query-client-provider");

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Disable automatic background refetch for better control
            refetchOnWindowFocus: false,
            // Keep data fresh for 5 minutes by default
            staleTime: 5 * 60 * 1000,
            // Cache data for 24 hours for offline support
            gcTime: 1000 * 60 * 60 * 24,
            // Retry once on failure
            retry: 1,
            // Use cached data first, then fetch when online
            networkMode: "offlineFirst",
          },
          mutations: {
            // Writes are not assumed to be idempotent. Retrying a committed
            // comment, payment, or toggle after a timeout can duplicate or
            // reverse user intent.
            retry: false,
            // Do not queue writes across an account or connectivity boundary.
            networkMode: "online",
          },
        },
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            // Log mutation errors for debugging
            logger.error(
              "[MutationCache] Failed:",
              mutation.options.mutationKey,
              error,
            );
          },
        }),
      }),
  );

  // Background uploads outlive the components that start them; give the
  // upload manager a handle for cache patches/invalidation on completion.
  useEffect(() => {
    registerQueryClient(queryClient);
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
